import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PromptService } from '../promtp-service/prompt.service';
import { SecureStorageService } from '../storage/secure-storage.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { EmotionApiConfiguration } from 'src/app/api/proxy/emotion/emotion-api-configuration';
import { consumerHistoryGet, consumerEmotionPost, consumerSupplierEmotionPost } from 'src/app/api/proxy/emotion/functions';
import { EmotionRequestModel, EmotionsHistoryResponse, EmotionType, SupplierEmotionRequestModel } from 'src/app/api/proxy/emotion/emotion-models';
import { StorageKey } from '../storage/models/storage-key.enum';
import { EventService } from '../events/event.service';
import { BaseApiService } from '../base-api-service/base-api.service';

/**
 * Non-Wellcrowd selectable emotions, in display order.
 * API value {@link EmotionType.Excited} is shown as "Energised" in the UI.
 */
export const STANDARD_EMOTION_TYPES: readonly EmotionType[] = [
  EmotionType.Happy,
  EmotionType.Excited,
  EmotionType.Calm,
  EmotionType.Tired,
  EmotionType.Stressed,
  EmotionType.Anxious,
  EmotionType.Frustrated,
  EmotionType.Sad,
];

@Injectable({
  providedIn: 'root',
})
export class EmotionService extends BaseApiService {
  private NETWORK_TIMEOUT = 25000;
  private cache: Map<string, EmotionsHistoryResponse[]> = new Map(); // Cache map to store responses
  public static MAX_CACHE_MONTHS = 12;

  constructor(
    protected _PromptService: PromptService,
    private http: HttpClient,
    private emotionCfg: EmotionApiConfiguration
  ) {
    super(_PromptService);
    setTimeout(async () => { await this.loadCacheFromStorage() }, 0);
  }

  private async loadCacheFromStorage(): Promise<void> {
    const storedMap = await SecureStorageService.get(SecureStorageKey.EmotionHistory);
    if (storedMap) {
      const parsedMap = JSON.parse(storedMap) as Record<string, EmotionsHistoryResponse[]>;
      this.cache = new Map(Object.entries(parsedMap));
    }
  }

  public async getEmotionHistoryStatic(): Promise<EmotionsHistoryResponse[] | null> {
    const emotionHistory = await SecureStorageService.get(StorageKey.EmotionHistory);
    let cacheTimestamp: Date | null = null;
    let silent = false;

    let model: EmotionsHistoryResponse[] | null = null;
    if (emotionHistory) {
      try {
        model = JSON.parse(emotionHistory);
      } catch {
        model = null;
      }
    }

    if (model) {
      const ts = (model as any).fetchedAt;
      if (ts) {
        cacheTimestamp = new Date(ts);
      }
      silent = true;
      EventService.emotionHistoryUpdated.emit(model);
    }
    return model;
  }

  public async getEmotionHistory(forced: boolean = false): Promise<EmotionsHistoryResponse[] | null> {
    await this.ensureCacheLoaded();
    let cacheTimestamp: Date | null = null;

    let model = await this.getEmotionHistoryStatic();

    if (model) {
      const ts = (model as any).fetchedAt;
      if (ts) {
        cacheTimestamp = new Date(ts);
      }
    }

    // Get yesterday's date with time set to 23:59:59 including timezone
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1); // Go back 1 day
    yesterday.setHours(23, 59, 59); // Set to 23:59:59.999

    if (model && cacheTimestamp && !forced) {
      const now = new Date();
      const isSameDay = cacheTimestamp.toDateString() === now.toDateString();
      if (isSameDay) {
        return model;
      }

    }

    const obs = consumerHistoryGet(this.http, this.emotionCfg.rootUrl);
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'getEmotionHistory');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      model = result;
      await this.updateHistoryCache(model);
    }
    return model;
  }

  private async ensureCacheLoaded(): Promise<void> {
    if (this.cache.size === 0) {
      await this.loadCacheFromStorage();
    }
  }

  public async isSelectionAllowed(): Promise<boolean> {
    let model = await this.getEmotionHistoryStatic();
    if (model) {
      // Find latest valid timestamp in one pass
      const latestTs = model.reduce<number | undefined>((max, h) => {
        if (!h.loggedDate) return max;
        const t = Date.parse(h.loggedDate);
        if (Number.isNaN(t)) return max;
        return max === undefined ? t : (t > max ? t : max);
      }, undefined);

      if (latestTs === undefined) return true; // or whatever default you prefer

      // Compare by calendar day (local)
      const latestStart = new Date(latestTs).setHours(0, 0, 0, 0);
      const todayStart = new Date().setHours(0, 0, 0, 0);

      return latestStart < todayStart;
    }
    else
      return true;
  }

  private async updateHistoryCache(results: EmotionsHistoryResponse[]) {

    (results as any).fetchedAt = new Date().toISOString();
    await SecureStorageService.set(StorageKey.EmotionHistory, JSON.stringify(results));
    EventService.emotionHistoryUpdated.emit(results);
  }

  public async SaveCurrentEmotion(emotionType: EmotionType): Promise<EmotionsHistoryResponse[] | null> {
    let request: EmotionRequestModel = {};
    request.emotion = emotionType;
    request.eventDate = new Date().toISOString();
    
    const obs = consumerEmotionPost(this.http, this.emotionCfg.rootUrl, { body: request });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'SaveCurrentEmotion');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      await this.updateHistoryCache(result);
      return result;
    }
    return null;
  }

  public async SaveSupplierEmotion(request: SupplierEmotionRequestModel): Promise<EmotionsHistoryResponse[] | null> {
    const obs = consumerSupplierEmotionPost(this.http, this.emotionCfg.rootUrl, { body: request });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'SaveSupplierEmotion');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      await this.updateHistoryCache(result);
      return result;
    }
    return null;
  }
}
